import { useEffect, useReducer } from 'react';
import firebase from 'firebase/app';
import 'firebase/auth';
import { _auth } from '../../firebase';
import { API_URL } from 'configuration/urls';
import { selectStoreConfig } from 'containers/Layout/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { LOGIN_FROM_TYPE, ORDER_STAGE_TYPES } from 'helpers/enums';
import { allCustomersFetched } from 'containers/Home/actions';
import { detectWebView } from '../webViewDetection';

const defaultState = {
  firstName: '',
  lastName: '',
  firebaseUser: null,
  idToken: null,
  loading: true,
  error: null,
  fireStoreUser: null,
  isOauthFlow: false,
  authOrderStage: ORDER_STAGE_TYPES.NONE,
  loginFrom: LOGIN_FROM_TYPE.MAIN,
  credential: null,
  isUserNamesExists: false,
  skipOnAuthStateChanged: false,
};

export const useFirebaseAuth = (callRegisteredFunction) => {
  const dispatch = useDispatch();
  const storeConfig = useSelector(selectStoreConfig);
  const [state, setState] = useReducer(
    (s, a) => ({ ...s, ...a }),
    defaultState
  );
  const {
    firstName,
    lastName,
    firebaseUser,
    idToken,
    loading,
    error,
    fireStoreUser,
    authOrderStage,
    loginFrom,
    credential,
    isOauthFlow,
    isUserNamesExists,
    skipOnAuthStateChanged,
  } = state;
  const { restaurantId } = storeConfig;
  const isOAuthAvailable = !detectWebView();

  const fetchAppUser = async (idToken) => {
    try {
      const res = await fetch(`${API_URL}api/v1/fetchUser`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ restaurantId }),
      });

      const data = await res.json();

      if (data.code === 200 && data.data?.user) {
        return { found: true, user: data.data.user };
      }

      if (data.code === 404 || !data.data?.user) {
        return { found: false };
      }

      if (data.errors?.length) {
        throw new Error(data.errors[0]);
      }

      throw new Error('Unknown error while fetching app user.');
    } catch (err) {
      console.error('Failed to fetch app user:', err);
      throw err; // Let the caller handle it
    }
  };

  useEffect(() => {
    const unsubscribe = _auth.onAuthStateChanged(async (user) => {
      if (skipOnAuthStateChanged) return;
      if (user) {
        const token = await user.getIdToken();
        const appUserResponse = await fetchAppUser(token);
        if (appUserResponse.found) {
          dispatch(allCustomersFetched(user, appUserResponse.user, []));
        }
        setState({ idToken: token, firebaseUser: user });
      } else {
        setState({ firebaseUser: null, fireStoreUser: null, idToken: null });
      }
      setState({ loading: false });
    });

    const exchangeAuthCode = async () => {
      try {
        const url = new URL(window.location.href);
        const authCode = url.searchParams.get('auth_code');

        if (!authCode) return;

        const res = await fetch(`${API_URL}api/v1/auth/exchange`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code: authCode }),
        });

        const data = await res.json();
        const customToken = data?.data?.customToken;

        if (customToken) {
          const result = await _auth.signInWithCustomToken(customToken);
          const token = await result.user.getIdToken();
          const appUserResponse = await fetchAppUser(token);

          if (!appUserResponse.found) {
            await _auth.signOut();
          }
        }

        url.searchParams.delete('auth_code');
        window.history.replaceState({}, '', url.toString());
      } catch (err) {
        console.error('Auth code exchange failed:', err);
        const url = new URL(window.location.href);
        url.searchParams.delete('auth_code');
        window.history.replaceState({}, '', url.toString());
      }
    };

    exchangeAuthCode();

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignIn = async (provider) => {
    setState({ isOauthFlow: true, error: null });
    try {
      const result = await firebase.auth().signInWithPopup(provider);
      setState({ loading: true });
      const credential = result.credential;
      const user = result.user;
      const token = await user.getIdToken();
      const appUserResponse = await fetchAppUser(token);
      setState({ credential, idToken: token });

      if (appUserResponse.found) {
        if (callRegisteredFunction)
          callRegisteredFunction(
            user,
            appUserResponse.user,
            ORDER_STAGE_TYPES.REVIEW
          );
      } else {
        let firstName = '';
        let lastName = '';

        if (user.displayName) {
          const nameParts = user.displayName.split(' ');
          firstName = nameParts[0];
          lastName = nameParts.slice(1).join(' ');
        }

        setState({
          firstName,
          lastName,
          authOrderStage: ORDER_STAGE_TYPES.MOBILE_FORM,
        });
      }
    } catch (err) {
      console.error('Sign-in error:', err);
      setState({ error: err });
    } finally {
      setState({ loading: false });
    }
  };

  const signInWithGoogle = () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    handleSignIn(provider);
  };

  const signInWithApple = () => {
    const provider = new firebase.auth.OAuthProvider('apple.com');
    handleSignIn(provider);
  };

  const signInWithFacebook = () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    handleSignIn(provider);
  };

  const getUserByMobileNumber = async (mobileNumber) => {
    setState({ loading: true });
    try {
      const res = await fetch(`${API_URL}getUserByMobileNumber`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobileNumber, restaurantId }),
      });

      const data = await res.json();
      return data.user || null;
    } catch (err) {
      console.error('Error getUserByMobileNumber failed:', err);
      setState({ error: err });
      throw err;
    } finally {
      setState({ loading: false });
    }
  };

  const continueWithMobile = async (mobileNumber) => {
    setState({ loading: true });
    try {
      const user = await getUserByMobileNumber(mobileNumber);
      if (user) {
        setState({
          authOrderStage: ORDER_STAGE_TYPES.VERIFY_FROM,
        });
      } else {
        setState({
          authOrderStage: ORDER_STAGE_TYPES.MOBILE_FORM,
        });
      }
    } catch (err) {
      console.error('Error during continueWithMobile:', err);
      setState({ error: err });
    } finally {
      setState({ loading: false });
    }
  };

  const signOut = async (callbackAfterSignOut) => {
    await _auth.signOut();

    if (typeof callbackAfterSignOut === 'function') {
      callbackAfterSignOut();
    }

    setState({
      firebaseUser: null,
      idToken: null,
      fireStoreUser: null,
    });
  };

  return {
    firstName,
    lastName,
    firebaseUser,
    fireStoreUser,
    idToken,
    loading,
    error,
    signInWithGoogle,
    signInWithApple,
    signInWithFacebook,
    signOut,
    getUserByMobileNumber,
    continueWithMobile,
    authOrderStage,
    loginFrom,
    credential,
    setState, // exposing setState if you want to manually update
    isOauthFlow,
    isUserNamesExists,
    isWebView: detectWebView(),
    isOAuthAvailable,
  };
};
